/*
Copyright 2026 SSH Communications Security

*/

/*
Legacy 1:1 screenshare handover patch.

The legacy MatrixCall flow assumes the screenshare transceiver can be torn down and
re-created cleanly. In Chromium-family browsers the sequence

1. User A shares
2. User A stops
3. User B starts sharing

can leave the call in a broken state:

- stopping A's share can fail because removeTrack() sees a stale sender
- starting B's share can reuse the right m-line, but the sender is not always fully
  rebound before renegotiation
- on A, the incoming screenshare stream can be created and then immediately emptied
  during the same renegotiation round

To keep that handover stable we:

- stop local share with replaceTrack(null) and keep the screenshare transceiver
  recvonly instead of destroying it
- explicitly rebind/reuse the screenshare transceiver when starting a new share
- repair remote screenshare metadata when the browser delivers a different stream id
- recover an emptied remote screenshare feed from the still-live receiver track
*/

import { MatrixCall } from "matrix-js-sdk/src/webrtc/call";
import { CallFeed } from "matrix-js-sdk/src/webrtc/callFeed";
import { type SDPStreamMetadata, SDPStreamMetadataPurpose } from "matrix-js-sdk/src/webrtc/callEventTypes";

type SetScreensharingEnabled = MatrixCall["setScreensharingEnabled"];
type ScreensharingOpts = Parameters<SetScreensharingEnabled>[1];
type PushLocalFeed = MatrixCall["pushLocalFeed"];
type OnMetadataChanged = MatrixCall["onSDPStreamMetadataChangedReceived"];
type OnNegotiateReceived = MatrixCall["onNegotiateReceived"];
type OnAnswerReceived = MatrixCall["onAnswerReceived"];
type MetadataChangedEvent = Parameters<OnMetadataChanged>[0];
type NegotiateEvent = Parameters<OnNegotiateReceived>[0];
type AnswerEvent = Parameters<OnAnswerReceived>[0];

type PatchedMatrixCallPrototype = {
    __sshLegacyCallScreensharePatchApplied?: boolean;
    createPeerConnection?: () => RTCPeerConnection;
    deleteFeedByStream?: (stream: MediaStream) => void;
    onAnswerReceived?: OnAnswerReceived;
    onNegotiateReceived?: OnNegotiateReceived;
    onSDPStreamMetadataChangedReceived?: OnMetadataChanged;
    pushLocalFeed: PushLocalFeed;
    pushRemoteFeed?: (stream: MediaStream) => void;
    setScreensharingEnabled: SetScreensharingEnabled;
};

type MatrixCallWithScreenshareInternals = {
    callId: string;
    client: {
        getDeviceId?: () => string | null;
        getUserId?: () => string | null;
        getMediaHandler: () => {
            getScreensharingStream: (opts?: ScreensharingOpts) => Promise<MediaStream | null>;
            stopScreensharingStream: (stream: MediaStream) => void;
        };
    };
    feeds: CallFeed[];
    getOpponentDeviceId: () => string | undefined;
    getOpponentMember: () => { userId: string } | undefined;
    getRemoteFeeds: () => CallFeed[];
    isScreensharing: () => boolean;
    opponentSupportsSDPStreamMetadata: () => boolean;
    peerConn?: RTCPeerConnection | null;
    pushRemoteFeed?: (stream: MediaStream) => void;
    remoteSDPStreamMetadata?: SDPStreamMetadata;
    roomId: string;
    transceivers: Map<string, RTCRtpTransceiver>;
    localScreensharingStream?: MediaStream;
    deleteFeedByStream: (stream: MediaStream) => void;
    pushLocalFeed: PushLocalFeed;
};

type SenderWithSetStreams = RTCRtpSender & {
    setStreams?: (...streams: MediaStream[]) => void;
};

const streamlessTrackPatchedPeerConnections = new WeakSet<RTCPeerConnection>();
const pendingRemoteScreenshareStreams = new WeakMap<MatrixCallWithScreenshareInternals, Set<MediaStream>>();
const pendingStreamlessRemoteTracks = new WeakMap<MatrixCallWithScreenshareInternals, Set<MediaStreamTrack>>();
const recoveredRemoteScreenshareTrackListeners = new WeakMap<MediaStreamTrack, () => void>();
const streamlessTrackListeners = new WeakMap<MediaStreamTrack, () => void>();

const getScreenshareTransceiverKey = (kind: "audio" | "video"): string =>
    `${SDPStreamMetadataPurpose.Screenshare}:${kind}`;

const getUsermediaTransceiverKey = (kind: "audio" | "video"): string =>
    `${SDPStreamMetadataPurpose.Usermedia}:${kind}`;

const isStoppedTransceiver = (transceiver: RTCRtpTransceiver): boolean =>
    transceiver.direction === "stopped" || transceiver.currentDirection === "stopped";

// When we reuse a recvonly transceiver for screenshare, Chromium behaves more
// consistently if the sender is explicitly rebound to the outgoing stream.
// This is best-effort because some browsers do not implement sender.setStreams().
const bindScreenshareSenderStreams = (
    call: MatrixCallWithScreenshareInternals,
    stream: MediaStream,
): void => {
    for (const track of stream.getTracks()) {
        if (track.kind !== "audio" && track.kind !== "video") continue;

        const sender = call.transceivers.get(getScreenshareTransceiverKey(track.kind))?.sender as SenderWithSetStreams | undefined;
        if (!sender?.setStreams) continue;

        try {
            sender.setStreams(stream);
        } catch {
            // Browsers that reject setStreams() still work with the rest of the patch.
        }
    }
};

// During renegotiation the browser may deliver the new remote screenshare stream
// before its final stream id is reflected in metadata. In that case we only want
// to reuse metadata when there is exactly one unmatched remote screenshare entry.
const findMissingRemoteScreenshareMetadata = (
    call: MatrixCallWithScreenshareInternals,
): { metadata: SDPStreamMetadata[string]; streamId: string } | null => {
    if (!call.remoteSDPStreamMetadata) return null;

    const remoteFeeds = call.getRemoteFeeds();
    const remoteFeedStreamIds = new Set(remoteFeeds.map((feed) => feed.stream.id));
    const existingRemoteScreenshare = remoteFeeds.some((feed) => feed.purpose === SDPStreamMetadataPurpose.Screenshare);

    const candidateEntries = Object.entries(call.remoteSDPStreamMetadata).filter(([streamId, metadata]) => {
        return metadata.purpose === SDPStreamMetadataPurpose.Screenshare && !remoteFeedStreamIds.has(streamId);
    });

    if (candidateEntries.length !== 1 || existingRemoteScreenshare) {
        return null;
    }

    const [streamId, metadata] = candidateEntries[0];
    return { streamId, metadata };
};

const hasRemoteTrackAlready = (call: MatrixCallWithScreenshareInternals, track: MediaStreamTrack): boolean => {
    return call.getRemoteFeeds().some((feed) => feed.stream.getTracks().some((existingTrack) => existingTrack.id === track.id));
};

const removePendingRemoteScreenshareStream = (call: MatrixCallWithScreenshareInternals, stream: MediaStream): void => {
    const pendingStreams = pendingRemoteScreenshareStreams.get(call);
    if (!pendingStreams) return;

    pendingStreams.delete(stream);
    if (pendingStreams.size === 0) {
        pendingRemoteScreenshareStreams.delete(call);
    }
};

const enqueuePendingRemoteScreenshareStream = (call: MatrixCallWithScreenshareInternals, stream: MediaStream): void => {
    const pendingStreams = pendingRemoteScreenshareStreams.get(call) ?? new Set<MediaStream>();
    pendingStreams.add(stream);
    pendingRemoteScreenshareStreams.set(call, pendingStreams);
};

const removePendingStreamlessRemoteTrack = (call: MatrixCallWithScreenshareInternals, track: MediaStreamTrack): void => {
    const pendingTracks = pendingStreamlessRemoteTracks.get(call);
    if (!pendingTracks) return;

    pendingTracks.delete(track);
    if (pendingTracks.size === 0) {
        pendingStreamlessRemoteTracks.delete(call);
    }
};

const enqueuePendingStreamlessRemoteTrack = (call: MatrixCallWithScreenshareInternals, track: MediaStreamTrack): void => {
    const pendingTracks = pendingStreamlessRemoteTracks.get(call) ?? new Set<MediaStreamTrack>();
    pendingTracks.add(track);
    pendingStreamlessRemoteTracks.set(call, pendingTracks);
};

const ensureIncomingRemoteScreenshareMetadata = (
    call: MatrixCallWithScreenshareInternals,
    stream: MediaStream,
): boolean => {
    if (call.remoteSDPStreamMetadata?.[stream.id]?.purpose) {
        removePendingRemoteScreenshareStream(call, stream);
        return true;
    }

    if (stream.getVideoTracks().length === 0) {
        return false;
    }

    const missingMetadata = findMissingRemoteScreenshareMetadata(call);
    if (!missingMetadata) {
        enqueuePendingRemoteScreenshareStream(call, stream);
        return false;
    }

    call.remoteSDPStreamMetadata = {
        ...call.remoteSDPStreamMetadata,
        [stream.id]: missingMetadata.metadata,
    };
    removePendingRemoteScreenshareStream(call, stream);
    return true;
};

const pushSyntheticRemoteScreenshareFeed = (call: MatrixCallWithScreenshareInternals, track: MediaStreamTrack): boolean => {
    if (track.kind !== "video") return false;
    if (!call.opponentSupportsSDPStreamMetadata()) return false;

    if (hasRemoteTrackAlready(call, track)) {
        removePendingStreamlessRemoteTrack(call, track);
        return true;
    }

    const missingMetadata = findMissingRemoteScreenshareMetadata(call);
    if (!missingMetadata || !call.pushRemoteFeed) {
        enqueuePendingStreamlessRemoteTrack(call, track);
        return false;
    }

    const syntheticStream = new MediaStream([track]);
    call.remoteSDPStreamMetadata = {
        ...call.remoteSDPStreamMetadata,
        [syntheticStream.id]: missingMetadata.metadata,
    };
    call.pushRemoteFeed(syntheticStream);
    removePendingStreamlessRemoteTrack(call, track);

    if (!streamlessTrackListeners.has(track)) {
        const onTrackEnded = (): void => {
            removePendingStreamlessRemoteTrack(call, track);
            call.deleteFeedByStream(syntheticStream);
            streamlessTrackListeners.delete(track);
            track.removeEventListener("ended", onTrackEnded);
        };

        streamlessTrackListeners.set(track, onTrackEnded);
        track.addEventListener("ended", onTrackEnded);
    }

    return true;
};

const flushPendingRemoteScreenshareStreams = (call: MatrixCallWithScreenshareInternals): void => {
    const pendingStreams = pendingRemoteScreenshareStreams.get(call);
    if (!pendingStreams?.size || !call.pushRemoteFeed) return;

    for (const stream of Array.from(pendingStreams)) {
        call.pushRemoteFeed(stream);
    }
};

const flushPendingStreamlessRemoteTracks = (call: MatrixCallWithScreenshareInternals): void => {
    const pendingTracks = pendingStreamlessRemoteTracks.get(call);
    if (!pendingTracks?.size) return;

    for (const track of Array.from(pendingTracks)) {
        pushSyntheticRemoteScreenshareFeed(call, track);
    }
};

const flushPendingRemoteScreenshareMedia = (call: MatrixCallWithScreenshareInternals): void => {
    flushPendingRemoteScreenshareStreams(call);
    flushPendingStreamlessRemoteTracks(call);
};

// Inbound legacy calls can have the recvonly screenshare transceiver living only
// on peerConn.getTransceivers(), not in call.transceivers. Recovery therefore has
// to check both places instead of trusting the SDK's bookkeeping alone.
const getLiveScreenshareReceiverTracks = (call: MatrixCallWithScreenshareInternals): MediaStreamTrack[] => {
    const tracks: MediaStreamTrack[] = [];
    const mappedUsermediaTransceivers = new Set<RTCRtpTransceiver>();

    for (const kind of ["video", "audio"] as const) {
        const mappedUsermediaTransceiver = call.transceivers.get(getUsermediaTransceiverKey(kind));
        if (mappedUsermediaTransceiver) {
            mappedUsermediaTransceivers.add(mappedUsermediaTransceiver);
        }
    }

    for (const kind of ["video", "audio"] as const) {
        let transceiver = call.transceivers.get(getScreenshareTransceiverKey(kind));
        let receiverTrack = transceiver?.receiver.track;

        if (!receiverTrack || receiverTrack.readyState === "ended") {
            const fallbackTransceiver = call.peerConn
                ?.getTransceivers()
                .find((candidate) => {
                    if (mappedUsermediaTransceivers.has(candidate)) return false;
                    if (isStoppedTransceiver(candidate)) return false;
                    if (candidate.receiver.track?.kind !== kind) return false;
                    if (!candidate.receiver.track || candidate.receiver.track.readyState === "ended") return false;
                    if (candidate.sender.track && candidate.sender.track.readyState !== "ended") return false;
                    return true;
                });

            if (fallbackTransceiver) {
                call.transceivers.set(getScreenshareTransceiverKey(kind), fallbackTransceiver);
                transceiver = fallbackTransceiver;
                receiverTrack = fallbackTransceiver.receiver.track;
            }
        }

        if (!receiverTrack || receiverTrack.readyState === "ended") continue;
        if (tracks.some((existingTrack) => existingTrack.id === receiverTrack.id)) continue;
        tracks.push(receiverTrack);
    }

    return tracks;
};

const attachRecoveredRemoteScreenshareTrackEndedListener = (
    call: MatrixCallWithScreenshareInternals,
    stream: MediaStream,
): void => {
    const videoTrack = stream.getVideoTracks()[0];
    if (!videoTrack || recoveredRemoteScreenshareTrackListeners.has(videoTrack)) return;

    const onTrackEnded = (): void => {
        recoveredRemoteScreenshareTrackListeners.delete(videoTrack);
        videoTrack.removeEventListener("ended", onTrackEnded);
        call.deleteFeedByStream(stream);
    };

    recoveredRemoteScreenshareTrackListeners.set(videoTrack, onTrackEnded);
    videoTrack.addEventListener("ended", onTrackEnded);
};

// Chromium can briefly empty the incoming screenshare MediaStream during the
// renegotiation that hands screensharing over from one participant to the other.
// If the receiver track is still alive, keep the feed and swap it onto a new
// stream built from that receiver track instead of deleting the feed outright.
const recoverRemoteScreenshareFeedBeforeDeletion = (
    call: MatrixCallWithScreenshareInternals,
    stream: MediaStream,
): boolean => {
    if (!call.opponentSupportsSDPStreamMetadata()) return false;
    if (stream.getTracks().length > 0) return false;

    const feed = call.getRemoteFeeds().find((candidateFeed) => {
        return candidateFeed.purpose === SDPStreamMetadataPurpose.Screenshare && candidateFeed.stream.id === stream.id;
    });
    if (!feed) return false;

    const replacementTracks = getLiveScreenshareReceiverTracks(call);
    if (replacementTracks.length === 0) return false;

    const recoveredStream = new MediaStream(replacementTracks);
    const previousMetadata = call.remoteSDPStreamMetadata?.[stream.id];
    if (previousMetadata) {
        call.remoteSDPStreamMetadata = {
            ...call.remoteSDPStreamMetadata,
            [recoveredStream.id]: previousMetadata,
        };
    }

    feed.setNewStream(recoveredStream);
    attachRecoveredRemoteScreenshareTrackEndedListener(call, recoveredStream);
    return true;
};

// We keep the existing screenshare m-line whenever possible so the second sharer
// reuses the receiver slot that was already negotiated instead of creating a fresh
// transceiver that the remote side may reject or stop.
const adoptReusableScreenshareTransceivers = (
    call: MatrixCallWithScreenshareInternals,
    stream: MediaStream,
): void => {
    if (!call.peerConn) return;

    for (const track of stream.getTracks()) {
        if (track.kind !== "audio" && track.kind !== "video") continue;

        const key = getScreenshareTransceiverKey(track.kind);
        const existingTransceiver = call.transceivers.get(key);
        if (existingTransceiver && !isStoppedTransceiver(existingTransceiver)) {
            continue;
        }

        if (existingTransceiver && isStoppedTransceiver(existingTransceiver)) {
            call.transceivers.delete(key);
        }

        const mappedTransceivers = new Set(call.transceivers.values());
        const reusableTransceiver = call.peerConn.getTransceivers().find((transceiver) => {
            if (mappedTransceivers.has(transceiver)) return false;
            if (isStoppedTransceiver(transceiver)) return false;
            if (transceiver.sender.track) return false;

            const transceiverKind = transceiver.receiver.track?.kind ?? transceiver.sender.track?.kind;
            return transceiverKind === track.kind;
        });

        if (reusableTransceiver) {
            call.transceivers.set(key, reusableTransceiver);
        }
    }
};

// The legacy SDK tried to remove the sender entirely here. That breaks the
// handover because the screenshare transceiver may be reused later by the remote
// participant. Replacing the track with null keeps the m-line alive for receive.
const stopSendingLocalScreenshare = async (call: MatrixCallWithScreenshareInternals): Promise<void> => {
    for (const kind of ["audio", "video"] as const) {
        const key = getScreenshareTransceiverKey(kind);
        const transceiver = call.transceivers.get(key);
        if (!transceiver?.sender) continue;

        if (isStoppedTransceiver(transceiver)) {
            call.transceivers.delete(key);
            continue;
        }

        try {
            await transceiver.sender.replaceTrack(null);
            transceiver.direction = "recvonly";
        } catch {
            call.transceivers.delete(key);
        }
    }
};

const attachLocalScreenshareTrack = async (
    call: MatrixCallWithScreenshareInternals,
    track: MediaStreamTrack,
    stream: MediaStream,
): Promise<void> => {
    const key = getScreenshareTransceiverKey(track.kind as "audio" | "video");
    let transceiver = call.transceivers.get(key);

    if (transceiver && isStoppedTransceiver(transceiver)) {
        call.transceivers.delete(key);
        transceiver = undefined;
    }

    if (transceiver?.sender) {
        const sender = transceiver.sender as SenderWithSetStreams;
        sender.setStreams?.(stream);

        await transceiver.sender.replaceTrack(track);
        transceiver.direction = transceiver.direction === "inactive" ? "sendonly" : "sendrecv";

        sender.setStreams?.(stream);
        return;
    }

    const newSender = call.peerConn!.addTrack(track, stream);
    const newTransceiver = call.peerConn!.getTransceivers().find((candidate) => candidate.sender === newSender);
    if (newTransceiver) {
        call.transceivers.set(key, newTransceiver);
    }
};

// We do the sender/transceiver setup before adding the local feed because the
// legacy pushLocalFeed path performs a non-awaited replaceTrack(). The explicit
// setup here avoids negotiating while the reused sender is still half-attached.
const startLocalScreensharingWithExplicitSenderSetup = async (
    call: MatrixCallWithScreenshareInternals,
    opts?: ScreensharingOpts,
): Promise<CallFeed> => {
    const stream = await call.client.getMediaHandler().getScreensharingStream(opts);
    if (!stream) {
        throw new Error("FailedToAcquireScreensharingStream");
    }

    try {
        adoptReusableScreenshareTransceivers(call, stream);
        bindScreenshareSenderStreams(call, stream);

        for (const track of stream.getTracks()) {
            if (track.kind !== "audio" && track.kind !== "video") continue;
            await attachLocalScreenshareTrack(call, track, stream);
        }

        bindScreenshareSenderStreams(call, stream);

        return new CallFeed({
            client: call.client as never,
            roomId: call.roomId,
            userId: call.client.getUserId?.() ?? "",
            deviceId: call.client.getDeviceId?.() ?? undefined,
            stream,
            purpose: SDPStreamMetadataPurpose.Screenshare,
            audioMuted: false,
            videoMuted: false,
        });
    } catch (error) {
        call.client.getMediaHandler().stopScreensharingStream(stream);
        throw error;
    }
};

const removeLocalScreenshareFeed = (call: MatrixCallWithScreenshareInternals): void => {
    if (!call.localScreensharingStream) return;

    call.client.getMediaHandler().stopScreensharingStream(call.localScreensharingStream);
    call.deleteFeedByStream(call.localScreensharingStream);
};

const attachStreamlessScreenshareTrackListener = (
    call: MatrixCallWithScreenshareInternals,
    peerConn: RTCPeerConnection,
): void => {
    if (streamlessTrackPatchedPeerConnections.has(peerConn)) return;
    streamlessTrackPatchedPeerConnections.add(peerConn);

    peerConn.addEventListener("track", (event) => {
        if (event.streams.length === 0) {
            pushSyntheticRemoteScreenshareFeed(call, event.track);
        }
    });
};

// Patch the legacy MatrixCall entry points that participate in screenshare
// handover: transceiver setup, remote feed adoption/recovery, and the public
// screensharing toggle itself.
export const patchLegacyCallScreenshareTeardown = (): void => {
    const proto = MatrixCall.prototype as unknown as PatchedMatrixCallPrototype;
    if (proto.__sshLegacyCallScreensharePatchApplied) {
        return;
    }
    proto.__sshLegacyCallScreensharePatchApplied = true;

    if (proto.createPeerConnection) {
        const originalCreatePeerConnection = proto.createPeerConnection;
        proto.createPeerConnection = function (this: MatrixCallWithScreenshareInternals): RTCPeerConnection {
            const peerConn = originalCreatePeerConnection.call(this as unknown as MatrixCall);
            attachStreamlessScreenshareTrackListener(this, peerConn);
            return peerConn;
        };
    }

    const originalPushLocalFeed = proto.pushLocalFeed;
    proto.pushLocalFeed = function (this: MatrixCallWithScreenshareInternals, callFeed: CallFeed, addToPeerConnection = true): void {
        if (addToPeerConnection && callFeed.purpose === SDPStreamMetadataPurpose.Screenshare) {
            adoptReusableScreenshareTransceivers(this, callFeed.stream);
            bindScreenshareSenderStreams(this, callFeed.stream);
        }

        originalPushLocalFeed.call(this as unknown as MatrixCall, callFeed, addToPeerConnection);

        if (addToPeerConnection && callFeed.purpose === SDPStreamMetadataPurpose.Screenshare) {
            bindScreenshareSenderStreams(this, callFeed.stream);
        }
    };

    if (proto.pushRemoteFeed) {
        const originalPushRemoteFeed = proto.pushRemoteFeed;
        proto.pushRemoteFeed = function (this: MatrixCallWithScreenshareInternals, stream: MediaStream): void {
            if (this.opponentSupportsSDPStreamMetadata() && !this.remoteSDPStreamMetadata?.[stream.id]?.purpose) {
                const metadataReady = ensureIncomingRemoteScreenshareMetadata(this, stream);
                if (!metadataReady) return;
            }

            originalPushRemoteFeed.call(this as unknown as MatrixCall, stream);
        };
    }

    if (proto.deleteFeedByStream) {
        const originalDeleteFeedByStream = proto.deleteFeedByStream;
        proto.deleteFeedByStream = function (this: MatrixCallWithScreenshareInternals, stream: MediaStream): void {
            if (recoverRemoteScreenshareFeedBeforeDeletion(this, stream)) {
                return;
            }

            originalDeleteFeedByStream.call(this as unknown as MatrixCall, stream);
        };
    }

    if (proto.onSDPStreamMetadataChangedReceived) {
        const originalOnMetadataChanged = proto.onSDPStreamMetadataChangedReceived;
        proto.onSDPStreamMetadataChangedReceived = function (
            this: MatrixCallWithScreenshareInternals,
            event: MetadataChangedEvent,
        ): void {
            originalOnMetadataChanged.call(this as unknown as MatrixCall, event);
            flushPendingRemoteScreenshareMedia(this);
        };
    }

    if (proto.onNegotiateReceived) {
        const originalOnNegotiateReceived = proto.onNegotiateReceived;
        proto.onNegotiateReceived = async function (
            this: MatrixCallWithScreenshareInternals,
            event: NegotiateEvent,
        ): Promise<void> {
            await originalOnNegotiateReceived.call(this as unknown as MatrixCall, event);
            flushPendingRemoteScreenshareMedia(this);
        };
    }

    if (proto.onAnswerReceived) {
        const originalOnAnswerReceived = proto.onAnswerReceived;
        proto.onAnswerReceived = async function (
            this: MatrixCallWithScreenshareInternals,
            event: AnswerEvent,
        ): Promise<void> {
            await originalOnAnswerReceived.call(this as unknown as MatrixCall, event);
            flushPendingRemoteScreenshareMedia(this);
        };
    }

    const originalSetScreensharingEnabled = proto.setScreensharingEnabled;
    proto.setScreensharingEnabled = async function (
        this: MatrixCallWithScreenshareInternals,
        enabled: boolean,
        opts?: ScreensharingOpts,
    ): Promise<boolean> {
        if (enabled && this.opponentSupportsSDPStreamMetadata() && !this.isScreensharing()) {
            try {
                const callFeed = await startLocalScreensharingWithExplicitSenderSetup(this, opts);
                this.pushLocalFeed(callFeed, false);
                return true;
            } catch (error) {
                if (
                    error instanceof Error &&
                    (error.message.includes("Permission denied by system") ||
                        error.message.includes("Could not start video source"))
                ) {
                    throw new Error("ScreenSharingPermissionDenied");
                }

                return false;
            }
        }

        if (!enabled && this.opponentSupportsSDPStreamMetadata() && this.isScreensharing()) {
            await stopSendingLocalScreenshare(this);
            removeLocalScreenshareFeed(this);
            return false;
        }

        return originalSetScreensharingEnabled.call(this as unknown as MatrixCall, enabled, opts);
    };
};
